import { useState } from 'react';
import Footer from '../components/Footer';

export default function TermsAndConditions() {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      content: "Maya Devi University strives to provide accurate and current information on its website. However, academic content including courses and programs is subject to continuous review, and may change in response to academic or regulatory requirements. The University reserves the right to modify or discontinue programs, update course content, and amend fees or Terms and Conditions at any time without prior notice."
    },
    {
      id: "terms-of-use",
      title: "Terms of Use",
      content: "By accessing and using the official website of Maya Devi University (\"Site\"), you agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use of the Site immediately. The University reserves the right to update or modify these Terms at any time. Continued use of the Site after changes are posted indicates your acceptance of those modifications."
    },
    {
      id: "disclaimer",
      title: "Disclaimer",
      content: "The Site may contain opinions and expressions posted by individuals which do not reflect the views of Maya Devi University. The University does not monitor or endorse user-generated content and disclaims liability for any errors, inaccuracies, or omissions. Users should exercise discretion before relying on any information posted on the Site or linked external websites."
    },
    {
      id: "online-conduct",
      title: "Rules for Online Conduct",
      content: (
        <>
          <p>You agree to use the Site lawfully and ethically. You must not post or transmit content that:</p>
          <ul className="list-disc pl-6 mt-2">
            <li>Is defamatory, threatening, hateful, or discriminatory.</li>
            <li>Infringes intellectual property rights.</li>
            <li>Contains obscene language, images, or commercial solicitations.</li>
            <li>Promotes illegal activities.</li>
          </ul>
          <p className="mt-2">Violating these rules may result in removal of content, restriction of access, or other actions as deemed appropriate by the University.</p>
        </>
      )
    },
    {
      id: "data-security",
      title: "Data Security",
      content: "Your personal information is treated with confidentiality. Access is restricted to authorized personnel who are required to maintain data privacy. Maya Devi University reserves the right to update its privacy and security policies at any time."
    },
    {
      id: "site-materials",
      title: "Use of Site Materials",
      content: "Maya Devi University grants users a non-exclusive, non-transferable license to access and use content for personal, non-commercial purposes. You may not copy, distribute, or exploit materials on the Site without written permission from the University or content owner."
    },
    {
      id: "limitation-of-use",
      title: "Limitation of Use",
      content: "The Site must be used for lawful purposes only. You may not upload content that infringes third-party rights or transmit unsolicited promotional materials. Maya Devi University reserves the right to remove any content and deny access at its sole discretion."
    },
    {
      id: "external-links",
      title: "Links to External Websites",
      content: "The Site may link to external websites for convenience. Maya Devi University is not responsible for the content or accuracy of external sites and does not endorse them."
    },
    {
      id: "jurisdiction",
      title: "Choice of Law and Jurisdiction",
      content: "These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from use of the Site shall be subject to the exclusive jurisdiction of the courts in Dehradun."
    },
    {
      id: "warranty",
      title: "Disclaimer of Warranty and Limitation of Liability",
      content: "The Site and its content are provided \"as is\" without warranties of any kind. Maya Devi University does not guarantee uninterrupted access, accuracy of information, or absence of viruses. The University shall not be liable for any direct or indirect damages arising from your use of the Site."
    },
    {
      id: "trademarks",
      title: "Trademarks",
      content: "All trademarks, logos, and names displayed on the Site are the property of Maya Devi University or respective third parties. Unauthorized use is prohibited."
    },
    {
      id: "indemnification",
      title: "Indemnification",
      content: "You agree to indemnify and hold harmless Maya Devi University, its officers, and staff from any claims or liabilities arising from your use of the Site or violation of these Terms."
    },
    {
      id: "account-security",
      title: "Account Security",
      content: "If you create an account on the Site, you are responsible for maintaining its confidentiality and all activities under your account. You must provide accurate registration information. The University may suspend or terminate accounts at its discretion."
    },
    {
      id: "force-majeure",
      title: "Force Majeure",
      content: "The University is not liable for any failure or delay in performance due to events beyond its control, including but not limited to technical failures, natural disasters, or legal changes."
    },
    {
      id: "general-provisions",
      title: "General Provisions",
      content: "Maya Devi University makes every effort to ensure the reliability and accuracy of the Site content. However, the University disclaims all warranties and assumes no liability for any loss or damage resulting from use of the Site. These Terms represent the full agreement between you and the University concerning website use."
    },
    {
      id: "copyright",
      title: "Copyright Complaints",
      content: "If you believe that content on this Site infringes your copyright, please notify the Registrar, Maya Devi University, with all relevant details."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-green-50 mt-[140px]">
      {/* Header */}
      <header className="bg-green-700 p-6 shadow-md">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl font-bold text-white">Terms and Conditions</h1>
          <h2 className="text-xl text-green-100 mt-2">Maya Devi University</h2>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto flex flex-col md:flex-row gap-8 p-6 flex-grow">
        {/* Sidebar Navigation */}
        <aside className="md:w-1/4">
          <div className="bg-white p-4 rounded-lg shadow-md sticky top-6">
            <h2 className="text-xl font-semibold text-green-800 mb-4">Quick Navigation</h2>
            <nav>
              <ul className="space-y-2">
                {sections.map(section => (
                  <li key={section.id}>
                    <a 
                      href={`#${section.id}`}
                      className="block p-2 rounded hover:bg-green-100 text-green-700 hover:text-green-900 transition-colors"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="md:w-3/4">
          <div className="bg-white p-8 rounded-lg shadow-md">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="mb-8">
                <h2 className="text-2xl font-bold text-green-800 mb-4">
                  {section.title}
                </h2>
                <div className="text-gray-700">
                  {section.content}
                </div>
              </section>
            ))}

            {/* Last Updated */}
            {/* <div className="mt-8 pt-4 border-t border-green-100 text-sm text-gray-500">
              <p>Last Modified: May 20, 2025</p>
            </div> */}
          </div>
        </main>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}